import React, { useState } from 'react';
import { Objective, OptimizerResult } from '../types';
import { CLASSES } from '../constants/landUseConfig';
import { fmtEur } from '../utils/formatters';
import { Play, TrendingUp, TrendingDown } from 'lucide-react';

interface Props {
  onRun: (objective: Objective, maxPct: number, excludedClasses: Set<string>) => OptimizerResult;
}

export const OptimizerPanel: React.FC<Props> = ({ onRun }) => {
  const [objective, setObjective] = useState<Objective>('revenue');
  const [maxPct, setMaxPct] = useState(75);
  const [excludedClasses, setExcludedClasses] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<OptimizerResult | null>(null);

  const handleRun = () => {
    const res = onRun(objective, maxPct, excludedClasses);
    setResult(res);
  };

  const toggleExclude = (classId: string) => {
    setExcludedClasses((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
      } else {
        next.add(classId);
      }
      return next;
    });
  };

  const allExcluded = excludedClasses.size === CLASSES.length;

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-lg shadow-md border border-indigo-200 p-4">
      <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
        <span className="text-xl">⚡</span> Optimization Engine
      </h2>

      <div className="space-y-2.5 mb-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
            Objective
          </label>
          <select
            value={objective}
            onChange={(e) => setObjective(e.target.value as Objective)}
            className="w-full p-2 text-xs border border-gray-300 rounded bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
          >
            <option value="revenue">💰 Maximize Revenue</option>
            <option value="ghg">🌱 Maximize GHG Sink</option>
            <option value="bio">🦋 Maximize Biodiversity</option>
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
            Max Parcels to Change (%)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              value={maxPct}
              onChange={(e) => setMaxPct(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="relative">
              <input
                type="number"
                min="10"
                max="100"
                step="5"
                value={maxPct}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= 10 && val <= 100) setMaxPct(val);
                }}
                onBlur={(e) => {
                  const val = Number(e.target.value);
                  if (val < 10) setMaxPct(10);
                  if (val > 100) setMaxPct(100);
                }}
                className="w-16 px-2 py-1 text-xs font-semibold text-center border border-gray-300 rounded bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">%</span>
            </div>
          </div>
          <div className="flex justify-between text-[9px] text-gray-500 mt-0.5">
            <span>10%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
            Exclude Classes
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {CLASSES.map((c) => (
              <button
                key={c.id}
                onClick={() => toggleExclude(c.id)}
                className={`p-1.5 text-[9px] font-semibold rounded border transition-all ${
                  excludedClasses.has(c.id)
                    ? 'bg-gray-200 border-gray-400 text-gray-500 line-through'
                    : 'bg-white border-gray-300 hover:border-indigo-400'
                }`}
                style={{
                  backgroundColor: excludedClasses.has(c.id) ? '#e5e7eb' : c.fill,
                  color: excludedClasses.has(c.id) ? '#6b7280' : c.ink,
                }}
              >
                {c.label.split(' ')[0]}
              </button>
            ))}
          </div>
          {allExcluded && (
            <p className="text-[9px] text-red-600 mt-1">⚠️ Cannot run with all classes excluded</p>
          )}
        </div>

        <button
          onClick={handleRun}
          disabled={allExcluded}
          className={`w-full flex items-center justify-center gap-2 p-2.5 text-xs font-bold rounded transition-all ${
            allExcluded
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-sm'
          }`}
        >
          <Play className="w-3.5 h-3.5" />
          RUN OPTIMIZER
        </button>
      </div>

      {result ? (
        <div className="bg-white rounded p-3 space-y-2 border border-gray-200">
          <p className="text-[9px] text-gray-600 border-b border-gray-200 pb-2">
            {getObjectiveLabel(objective)} · Changed {result.changedIds.size} parcels 
            (cap: {maxPct}%) — see ★ on map
          </p>

          <DeltaRow
            label="Revenue"
            value={`€${fmtEur(result.after.totalEcon)}/yr`}
            delta={result.after.totalEcon - result.before.totalEcon}
            suffix="€"
            higherIsBetter={true}
            icon="💰"
          />
          <DeltaRow
            label="GHG"
            value={`${result.after.totalCarbon >= 0 ? '+' : ''}${result.after.totalCarbon.toFixed(1)} tCO₂e`}
            delta={result.after.totalCarbon - result.before.totalCarbon}
            suffix=" tCO₂e"
            higherIsBetter={false}
            icon="🌱"
          />
          <DeltaRow
            label="Bio"
            value={`${result.after.avgBio.toFixed(1)}%`}
            delta={result.after.avgBio - result.before.avgBio}
            suffix="%"
            higherIsBetter={true}
            icon="🦋"
          />

          <p className="text-[9px] text-gray-600 pt-2 border-t border-gray-200">
            💡 Press RECOMPUTE to lock changes or edit parcels manually.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded p-3 text-[10px] text-gray-600 leading-relaxed border border-gray-200">
          <p className="mb-1.5 font-semibold">How it works:</p>
          <p>Identifies parcels with highest gain for your objective, then reassigns them (up to cap) to maximize performance.</p>
        </div>
      )}
    </div>
  );
};

const DeltaRow: React.FC<{
  label: string;
  value: string;
  delta: number;
  suffix: string;
  higherIsBetter: boolean;
  icon: string;
}> = ({ label, value, delta, suffix, higherIsBetter, icon }) => {
  const good = higherIsBetter ? delta >= 0 : delta <= 0;
  const sign = delta >= 0 ? '+' : '';
  const Icon = good ? TrendingUp : TrendingDown;

  return (
    <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{icon}</span>
        <span className="text-[10px] font-medium text-gray-700">{label}</span>
      </div>
      <div className="text-right">
        <div className="text-xs font-bold font-mono text-gray-900">{value}</div>
        <div className={`text-[9px] flex items-center justify-end gap-0.5 ${good ? 'text-green-600' : 'text-red-600'}`}>
          <Icon className="w-2.5 h-2.5" />
          {sign}{Math.abs(delta).toFixed(1)}{suffix}
        </div>
      </div>
    </div>
  );
};

function getObjectiveLabel(obj: Objective): string {
  return {
    revenue: 'Max Revenue',
    ghg: 'Max GHG Sink',
    bio: 'Max Biodiversity',
  }[obj];
}